import type { Hotkey } from "@replo/design-system/utils/hotkeys";

import * as React from "react";

import { handleTransformWillChange } from "@v1/components/canvas/canvas-actions";
import { INITIAL_CANVAS_SCALE } from "@v1/components/canvas/canvas-constants";
import {
  selectActiveCanvas,
  selectCanvasInteractionMode,
  setCanvasInteractionMode,
} from "@v1/components/canvas/canvas-reducer";
import { useCanvasZoom } from "@v1/components/canvas/useCanvasZoom";
import {
  infoToast,
  successToast,
} from "@v1/components/designSystem/alert/Toast";
import { useRichTextComponent } from "@v1/components/RichTextComponentContext";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useContextMenuActions from "@v1/hooks/useContextMenuActions";
import useGetFlexboxWidthOrHeightOnChange from "@v1/hooks/useGetFlexboxWidthOrHeightOnChange";
import useOpenCodeEditor from "@v1/hooks/useOpenCodeEditor";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useVisibleBreakpoints } from "@v1/hooks/useVisibleBreakpoints";
import { useV1AIStreaming } from "@v1/providers/AIStreamingProvider";
import {
  redoOperation,
  selectComponentDataMapping,
  selectDraftComponentId,
  selectDraftComponentIds,
  selectDraftComponentName,
  selectDraftComponentOrAncestorVariantId,
  selectDraftComponentType,
  selectEditorMode,
  selectFontSize,
  selectFontStyle,
  selectFontWeight,
  selectIsParentGrid,
  selectLetterSpacing,
  selectLineHeight,
  selectParentFlexDirection,
  selectProjectId,
  selectTextDecoration,
  selectValidNextOperation,
  selectValidPreviousOperation,
  setEditorMode,
  undoOperation,
} from "@v1/reducers/core-reducer";
import {
  selectIsRichTextEditorFocused,
  setOpenPopoverId,
  setRightBarActiveTab,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorStore } from "@v1/store/store";
import { canvasToStyleMap } from "@v1/utils/editor";
import { useModal } from "@v2/store/modalStore";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

export function useGlobalEditorActions() {
  const store = useEditorStore();
  const dispatch = useEditorDispatch();
  const navigate = useNavigate();
  // TODO (Noah, 2022-02-07): These actions are no longer really specific to
  // the context menu - they should be moved out of this hook and just made
  // available as either util functions (e.g. canPaste) or ComponentActionTypes
  // (e.g. paste) or normal redux actions (e.g. copy)
  const contextMenuActions = useContextMenuActions();
  const setDraftElement = useSetDraftElement();
  const modal = useModal();
  const {
    isBuildAssistantMinimized: isBuildAssistantOpen,
    setIsBuildAssistantMinimized: setIsBuildAssistantOpen,
  } = useV1AIStreaming();
  const openCodeEditor = useOpenCodeEditor();
  const onFlexboxWidthOrHeightChange = useGetFlexboxWidthOrHeightOnChange();
  const { handleCanvasZoom, handleCanvasZoomIn, handleCanvasZoomOut } =
    useCanvasZoom();
  const applyComponentAction = useApplyComponentAction();
  const { onChangeVisibility } = useVisibleBreakpoints();
  const { setTag, toggleBulletList, toggleNumberedList } =
    useRichTextComponent();

  const handlePaste = React.useCallback(
    (event?: ClipboardEvent) => {
      const isRichTextEditorFocused = selectIsRichTextEditorFocused(
        store.getState(),
      );
      if (
        isRichTextEditorFocused ||
        [HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement].some(
          (elementType) => {
            return event?.target instanceof elementType;
          },
        )
      ) {
        return;
      }

      void contextMenuActions?.handlePaste(
        "normal",
        event?.clipboardData ?? null,
      );
    },
    [contextMenuActions, store],
  );

  const handleCopy = React.useCallback(
    (event?: ClipboardEvent) => {
      const draftComponentIds = selectDraftComponentIds(store.getState());
      const selection = document.getSelection()?.toString();
      if (selection) {
        return;
      }

      if (draftComponentIds.length > 0) {
        contextMenuActions?.handleCopy(
          draftComponentIds,
          event?.clipboardData ?? null,
        );
      }
      event?.preventDefault();
    },
    [contextMenuActions, store],
  );

  const handleDelete = React.useCallback(() => {
    const draftComponentIds = selectDraftComponentIds(store.getState());
    if (draftComponentIds.length > 1) {
      contextMenuActions?.handleGroupDelete(draftComponentIds);
    } else if (draftComponentIds.length > 0) {
      contextMenuActions?.handleDelete(draftComponentIds[0]!, "hotkey");
    }
  }, [store, contextMenuActions]);

  const handleDuplicate = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    if (draftComponentId) {
      contextMenuActions?.handleDuplicate(draftComponentId);
    }
  }, [contextMenuActions, store]);

  const handleCopyStyles = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    if (draftComponentId) {
      const activeDraftComponentOrAncestorVariantId =
        selectDraftComponentOrAncestorVariantId(store.getState());
      contextMenuActions?.handleCopyStyles(
        draftComponentId,
        activeDraftComponentOrAncestorVariantId,
      );
    }
  }, [contextMenuActions, store]);

  const handlePasteStyles = React.useCallback(() => {
    void contextMenuActions?.handlePaste("alt");
  }, [contextMenuActions]);

  const handleGroupIntoContainer = React.useCallback(() => {
    const draftComponentIds = selectDraftComponentIds(store.getState());
    if (draftComponentIds.length > 0) {
      contextMenuActions?.handleGroupContainer(draftComponentIds);
    }
  }, [contextMenuActions, store]);

  const handleTogglePreviewMode = React.useCallback(() => {
    const editorMode = selectEditorMode(store.getState());
    const isEditMode = editorMode === "edit";
    dispatch(setEditorMode(isEditMode ? "preview" : "edit"));
  }, [dispatch, store]);

  const handleToggleBuildAssistantMinimized = React.useCallback(() => {
    if (isBuildAssistantOpen) {
      setIsBuildAssistantOpen(false);
    }
    // Note (Evan, 2024-07-16) If the AI menu is enabled, it's not a modal,
    // so we can just toggle without any additional checks or anything
    else {
      setIsBuildAssistantOpen(true);
    }
  }, [isBuildAssistantOpen, setIsBuildAssistantOpen]);

  const handleToggleCodeEditor = React.useCallback(() => {
    if (openCodeEditor) {
      openCodeEditor?.();
    } else {
      infoToast(
        "Code Editor not available",
        "To open the code editor, please select a custom HTML, liquid, or integration component.",
      );
    }
  }, [openCodeEditor]);

  const handleOpenProjectSettings = React.useCallback(() => {
    const projectId = selectProjectId(store.getState());
    const path = generatePath(routes.editor.settings, { projectId });
    navigate(path);
  }, [navigate, store]);

  const handleMoveUpInTheTree = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    if (draftComponentId) {
      const componentDataMapping = selectComponentDataMapping(store.getState());
      const draftComponentData = componentDataMapping[draftComponentId];
      const ancestors = draftComponentData?.ancestorComponentData;
      if (ancestors) {
        const nearAncestor = ancestors[ancestors.length - 1];
        const nearAncestorId = nearAncestor?.[0];
        if (nearAncestorId) {
          setDraftElement({ componentIds: [nearAncestorId] });
        }
      }
    }
  }, [store, setDraftElement]);

  const handleMoveDownInTheTree = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    if (draftComponentId) {
      const componentDataMapping = selectComponentDataMapping(store.getState());
      const draftComponentData = componentDataMapping[draftComponentId];
      const descendants = draftComponentData?.containedComponentData;
      if (descendants) {
        const nearDescendant = descendants[0];
        const nearDescendantId = nearDescendant?.[0];
        if (nearDescendantId) {
          setDraftElement({ componentIds: [nearDescendantId] });
        }
      }
    }
  }, [store, setDraftElement]);

  const handleOpenExportToSectionModal = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    if (draftComponentId) {
      modal.openModal({
        type: "exportToSectionModal",
      });
    }
  }, [store, modal]);

  const handleSetWidthToFillAvailable = React.useCallback(
    () => onFlexboxWidthOrHeightChange("fill", "width"),
    [onFlexboxWidthOrHeightChange],
  );

  const handleSetWidthToWrapContent = React.useCallback(
    () => onFlexboxWidthOrHeightChange("wrap", "width"),
    [onFlexboxWidthOrHeightChange],
  );

  const handleSetHeightToFillAvailable = React.useCallback(
    () => onFlexboxWidthOrHeightChange("fill", "height"),
    [onFlexboxWidthOrHeightChange],
  );

  const handleSetHeightToWrapContent = React.useCallback(
    () => onFlexboxWidthOrHeightChange("wrap", "height"),
    [onFlexboxWidthOrHeightChange],
  );

  const handleUndo = React.useCallback(() => {
    const hasPreviousOperation = selectValidPreviousOperation(store.getState());
    if (hasPreviousOperation) {
      dispatch(undoOperation());
    }
  }, [dispatch, store]);

  const handleRedo = React.useCallback(() => {
    const hasNextOperation = selectValidNextOperation(store.getState());
    if (hasNextOperation) {
      dispatch(redoOperation());
    }
  }, [dispatch, store]);

  const handleSaveComponentTemplate = React.useCallback(() => {
    const draftComponentId = selectDraftComponentId(store.getState());
    const draftComponentName = selectDraftComponentName(store.getState());
    if (draftComponentId) {
      modal.openModal({
        type: "saveTemplateModal",
        props: { initialName: draftComponentName },
      });
    }
  }, [store, modal]);

  const handleMockSave = React.useCallback(() => {
    successToast(`Replo always saves changes automatically ✌️`, "");
  }, []);

  const handleResetZoom = React.useCallback(() => {
    handleCanvasZoom(INITIAL_CANVAS_SCALE);
  }, [handleCanvasZoom]);

  const handleSetDesignPanel = React.useCallback(() => {
    dispatch(setRightBarActiveTab("design"));
  }, [dispatch]);

  const handleSetConfigPanel = React.useCallback(() => {
    dispatch(setRightBarActiveTab("custom"));
  }, [dispatch]);

  const handleSetInteractionsPanel = React.useCallback(() => {
    dispatch(setRightBarActiveTab("interactions"));
  }, [dispatch]);

  const handleGrabCanvas = React.useCallback(
    (event?: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => {
      if (selectCanvasInteractionMode(store.getState()) === "locked") {
        return;
      }
      if (!event) {
        return;
      }
      const { type } = event;
      dispatch(handleTransformWillChange());
      dispatch(
        setCanvasInteractionMode(type === "keydown" ? "readyToGrab" : "edit"),
      );
    },
    [dispatch, store],
  );

  const handleToggleVisibility = React.useCallback(() => {
    const activeCanvas = selectActiveCanvas(store.getState());
    onChangeVisibility([canvasToStyleMap[activeCanvas]]);
  }, [onChangeVisibility, store]);

  const handleToggleBoldText = React.useCallback(() => {
    const draftComponentType = selectDraftComponentType(store.getState());
    if (draftComponentType === "text") {
      const fontWeight = selectFontWeight(store.getState());
      applyComponentAction({
        type: "setStyles",
        value: {
          fontWeight: fontWeight === "700" ? null : "700",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleToggleItalicText = React.useCallback(() => {
    const draftComponentType = selectDraftComponentType(store.getState());
    const fontStyle = selectFontStyle(store.getState());

    if (draftComponentType === "text") {
      applyComponentAction({
        type: "setStyles",
        value: {
          fontStyle: fontStyle === "italic" ? null : "italic",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleToggleUnderlineText = React.useCallback(() => {
    const draftComponentType = selectDraftComponentType(store.getState());
    const textDecoration = selectTextDecoration(store.getState());

    if (draftComponentType === "text") {
      applyComponentAction({
        type: "setStyles",
        value: {
          textDecoration: textDecoration === "underline" ? null : "underline",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleToggleStrikethroughText = React.useCallback(() => {
    const draftComponentType = selectDraftComponentType(store.getState());
    const textDecoration = selectTextDecoration(store.getState());

    if (draftComponentType === "text") {
      applyComponentAction({
        type: "setStyles",
        value: {
          textDecoration: textDecoration ? null : "line-through",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleToggleLinkText = React.useCallback(() => {
    dispatch(setOpenPopoverId("tiptap-toolbar-link"));
  }, [dispatch]);

  const handleToggleH1Text = React.useCallback(() => {
    setTag("1");
  }, [setTag]);

  const handleToggleH2Text = React.useCallback(() => {
    setTag("2");
  }, [setTag]);

  const handleToggleH3Text = React.useCallback(() => {
    setTag("3");
  }, [setTag]);

  const handleToggleH4Text = React.useCallback(() => {
    setTag("4");
  }, [setTag]);

  const handleToggleH5Text = React.useCallback(() => {
    setTag("5");
  }, [setTag]);

  const handleToggleH6Text = React.useCallback(() => {
    setTag("6");
  }, [setTag]);

  const handleToggleBulletList = React.useCallback(() => {
    toggleBulletList();
  }, [toggleBulletList]);

  const handleToggleNumberedList = React.useCallback(() => {
    toggleNumberedList();
  }, [toggleNumberedList]);

  const getFontSizeData = React.useCallback(() => {
    const fontSize = selectFontSize(store.getState());
    const { value, unit } = parseUnit(
      !isMixedStyleValue(fontSize) ? fontSize ?? 16 : 16,
      { value: "", unit: "" },
      "default",
      "px",
    );
    const newValue = typeof value === "string" ? Number.parseInt(value) : value;

    return {
      value: newValue,
      unit,
    };
  }, [store]);

  const adjustFontSize = React.useCallback(
    (type: "decrease" | "increase") => {
      const { value, unit } = getFontSizeData();

      let newFontSize;
      if (type === "decrease") {
        if (value === 1) {
          return;
        }
        newFontSize = Math.max(1, value - 1);
      } else {
        newFontSize = value + 1;
      }

      applyComponentAction({
        type: "setStyles",
        value: {
          fontSize: `${newFontSize}${unit}`,
        },
      });
    },
    [applyComponentAction, getFontSizeData],
  );

  const handleDecreaseFontSize = React.useCallback(() => {
    adjustFontSize("decrease");
  }, [adjustFontSize]);

  const handleIncreaseFontSize = React.useCallback(() => {
    adjustFontSize("increase");
  }, [adjustFontSize]);

  const getLetterSpacingData = React.useCallback(() => {
    const letterSpacing = selectLetterSpacing(store.getState());
    const { value, unit } = parseUnit(
      !isMixedStyleValue(letterSpacing) ? letterSpacing ?? 0 : 0,
      { value: "", unit: "" },
      "default",
      "px",
    );
    const newValue = typeof value === "string" ? Number.parseInt(value) : value;

    return {
      value: newValue,
      unit,
    };
  }, [store]);

  const adjustLetterSpacing = React.useCallback(
    (type: "decrease" | "increase") => {
      const { value, unit } = getLetterSpacingData();

      let newLetterSpacing;
      if (type === "decrease") {
        if (value === 0) {
          return;
        } else if (value === 1) {
          newLetterSpacing = null;
        } else {
          newLetterSpacing = value - 1;
        }
      } else {
        newLetterSpacing = value + 1;
      }

      applyComponentAction({
        type: "setStyles",
        value: {
          letterSpacing:
            newLetterSpacing !== null
              ? `${newLetterSpacing}${unit}`
              : newLetterSpacing,
        },
      });
    },
    [applyComponentAction, getLetterSpacingData],
  );

  const handleDecreaseLetterSpacing = React.useCallback(() => {
    adjustLetterSpacing("decrease");
  }, [adjustLetterSpacing]);

  const handleIncreaseLetterSpacing = React.useCallback(() => {
    adjustLetterSpacing("increase");
  }, [adjustLetterSpacing]);

  const getLineHeightData = React.useCallback(() => {
    const lineHeight = selectLineHeight(store.getState());
    const { value, unit } = parseUnit(
      !isMixedStyleValue(lineHeight) ? lineHeight ?? 0 : 0,
      { value: "", unit: "" },
      "default",
      "px",
    );
    const newValue = typeof value === "string" ? Number.parseInt(value) : value;

    return {
      value: newValue,
      unit,
    };
  }, [store]);

  const adjustLineHeight = React.useCallback(
    (type: "decrease" | "increase") => {
      const { value, unit } = getLineHeightData();

      let newLineHeight;
      if (type === "decrease") {
        if (value === 0) {
          return;
        } else if (value === 1) {
          newLineHeight = null;
        } else {
          newLineHeight = value - 1;
        }
      } else {
        newLineHeight = value + 1;
      }

      applyComponentAction({
        type: "setStyles",
        value: {
          lineHeight:
            newLineHeight !== null ? `${newLineHeight}${unit}` : newLineHeight,
        },
      });
    },
    [applyComponentAction, getLineHeightData],
  );

  const handleDecreaseLineHeight = React.useCallback(() => {
    adjustLineHeight("decrease");
  }, [adjustLineHeight]);

  const handleIncreaseLineHeight = React.useCallback(() => {
    adjustLineHeight("increase");
  }, [adjustLineHeight]);

  const adjustFontWeight = React.useCallback(
    (type: "decrease" | "increase") => {
      const fontWeights = [100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
      const fontWeight = selectFontWeight(store.getState());
      const currentFontWeight = fontWeight ? Number(fontWeight) : 400;
      let currentIndex = fontWeights.indexOf(currentFontWeight);

      if (type === "decrease") {
        if (currentIndex > 0) {
          currentIndex -= 1;
        } else {
          return;
        }
      } else {
        if (currentIndex < fontWeights.length - 1) {
          currentIndex += 1;
        } else {
          return;
        }
      }

      const newFontWeight = fontWeights[currentIndex];

      applyComponentAction({
        type: "setStyles",
        value: {
          fontWeight: newFontWeight,
        },
      });
    },
    [applyComponentAction, store],
  );

  const handleDecreaseFontWeight = React.useCallback(() => {
    adjustFontWeight("decrease");
  }, [adjustFontWeight]);

  const handleIncreaseFontWeight = React.useCallback(() => {
    adjustFontWeight("increase");
  }, [adjustFontWeight]);

  const handleAlignLeft = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (!(parentIsGrid || parentFlexDirection === "row")) {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "flex-start",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleAlignRight = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (!(parentIsGrid || parentFlexDirection === "row")) {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "flex-end",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleAlignHorizontalCenter = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (!(parentIsGrid || parentFlexDirection === "row")) {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "center",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleAlignTop = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (parentIsGrid || parentFlexDirection === "row") {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "flex-start",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleAlignBottom = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (parentIsGrid || parentFlexDirection === "row") {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "flex-end",
        },
      });
    }
  }, [applyComponentAction, store]);

  const handleAlignVerticalCenter = React.useCallback(() => {
    const parentIsGrid = selectIsParentGrid(store.getState());
    const parentFlexDirection = selectParentFlexDirection(store.getState());

    if (parentIsGrid || parentFlexDirection === "row") {
      applyComponentAction({
        type: "setStyles",
        value: {
          alignSelf: "center",
        },
      });
    }
  }, [applyComponentAction, store]);

  return {
    toggleH1Text: handleToggleH1Text,
    toggleH2Text: handleToggleH2Text,
    toggleH3Text: handleToggleH3Text,
    toggleH4Text: handleToggleH4Text,
    toggleH5Text: handleToggleH5Text,
    toggleH6Text: handleToggleH6Text,
    toggleBoldText: handleToggleBoldText,
    toggleLinkText: handleToggleLinkText,
    toggleItalicText: handleToggleItalicText,
    toggleUnderlineText: handleToggleUnderlineText,
    toggleStrikethroughText: handleToggleStrikethroughText,
    toggleBuildAssistantMinimized: handleToggleBuildAssistantMinimized,
    toggleBulletList: handleToggleBulletList,
    toggleNumberedList: handleToggleNumberedList,
    decreaseFontSize: handleDecreaseFontSize,
    increaseFontSize: handleIncreaseFontSize,
    decreaseLetterSpacing: handleDecreaseLetterSpacing,
    increaseLetterSpacing: handleIncreaseLetterSpacing,
    decreaseLineHeight: handleDecreaseLineHeight,
    increaseLineHeight: handleIncreaseLineHeight,
    decreaseFontWeight: handleDecreaseFontWeight,
    increaseFontWeight: handleIncreaseFontWeight,
    undo: handleUndo,
    redo: handleRedo,
    copy: handleCopy,
    copyStyles: handleCopyStyles,
    paste: handlePaste,
    pasteStyles: handlePasteStyles,
    delete: handleDelete,
    duplicate: handleDuplicate,
    groupIntoContainer: handleGroupIntoContainer,
    saveComponentTemplate: handleSaveComponentTemplate,
    mockSave: handleMockSave,
    exportToSection: handleOpenExportToSectionModal,
    setWidthToFillAvailable: handleSetWidthToFillAvailable,
    setWidthToWrapContent: handleSetWidthToWrapContent,
    setHeightToFillAvailable: handleSetHeightToFillAvailable,
    setHeightToWrapContent: handleSetHeightToWrapContent,
    toggleCodeEditor: handleToggleCodeEditor,
    toggleVisibility: handleToggleVisibility,
    togglePreviewMode: handleTogglePreviewMode,
    grabCanvas: handleGrabCanvas,
    setDesignPanel: handleSetDesignPanel,
    setConfigPanel: handleSetConfigPanel,
    setInteractionsPanel: handleSetInteractionsPanel,
    openProjectSettings: handleOpenProjectSettings,
    moveUpInTheTree: handleMoveUpInTheTree,
    moveDownInTheTree: handleMoveDownInTheTree,
    zoomIn: handleCanvasZoomIn,
    zoomOut: handleCanvasZoomOut,
    resetZoom: handleResetZoom,
    alignLeft: handleAlignLeft,
    alignRight: handleAlignRight,
    alignTop: handleAlignTop,
    alignBottom: handleAlignBottom,
    alignHorizontalCenter: handleAlignHorizontalCenter,
    alignVerticalCenter: handleAlignVerticalCenter,
  } satisfies Partial<Record<Hotkey, Function>>;
}
